import React from 'react';
import { DatePicker, Form, Input, Modal, Radio, Select, Spin } from 'antd';
import { DOCUMENT_UI_DATETIME_FORMAT } from '../../../../utils/documentUiFormat';
import { CLOSING_PACKAGE_MODAL_TITLES } from '../../workspace/closingPackageModalKinds';
import { useClientApprovalModal } from '../../hooks/useClientApprovalModal';
import {
  CLIENT_APPROVAL_RESULT_OPTIONS,
  CLIENT_APPROVAL_SOURCE_OPTIONS,
  isClientApprovalCommentRequired,
} from '../../lib/clientApprovalModalValidation';
import ClosingPackageTaskModalContext from './ClosingPackageTaskModalContext';
import ClosingPackageTaskModalEventDateHint from './ClosingPackageTaskModalEventDateHint';
import ClosingPackageTaskModalFooter from './ClosingPackageTaskModalFooter';
import type { ClosingPackageTaskModalBaseProps } from './ClosingPackageFormationModal.types';

export type ClosingPackageClientApprovalModalProps = ClosingPackageTaskModalBaseProps & {
  onSuccess?: () => void | Promise<void>;
};

/**
 * IMP-001H-3 — WAIT_CLIENT_APPROVAL workspace modal (client approval decision).
 */
const ClosingPackageClientApprovalModal: React.FC<ClosingPackageClientApprovalModalProps> = ({
  open,
  onClose,
  applicationId,
  onSuccess,
}) => {
  const { form, projection, contextView, contextLoading, submitting, handleSubmit } =
    useClientApprovalModal({
      applicationId,
      open,
      onClose,
      onSuccess,
    });

  const approvalResult = Form.useWatch('result', form);
  const commentRequired = isClientApprovalCommentRequired(approvalResult);

  return (
    <Modal
      title={CLOSING_PACKAGE_MODAL_TITLES['client-approval']}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={760}
      data-testid="client-approval-modal"
      footer={
        <ClosingPackageTaskModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Зарегистрировать ответ"
          submitTestId="client-approval-submit"
        />
      }
    >
      {contextLoading || !projection ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          <ClosingPackageTaskModalContext contextView={contextView} />

          <Form form={form} layout="vertical">
            <Form.Item
              name="registeredAt"
              label="Дата ответа клиента"
              rules={[{ required: true, message: 'Укажите дату ответа клиента' }]}
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                data-testid="client-approval-date"
                format={DOCUMENT_UI_DATETIME_FORMAT}
              />
            </Form.Item>

            <Form.Item
              name="result"
              label="Результат согласования"
              rules={[{ required: true, message: 'Выберите результат согласования' }]}
            >
              <Radio.Group data-testid="client-approval-result">
                {CLIENT_APPROVAL_RESULT_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="source"
              label="Способ получения ответа"
              rules={[{ required: true, message: 'Укажите способ получения ответа' }]}
            >
              <Select
                options={CLIENT_APPROVAL_SOURCE_OPTIONS}
                placeholder="Выберите способ"
                data-testid="client-approval-source"
              />
            </Form.Item>

            {approvalResult === 'REJECTED' ? (
              <Form.Item
                name="rejectionReason"
                label="Причина доработки"
                rules={[
                  { required: true, message: 'Укажите причину доработки' },
                  { whitespace: true, message: 'Причина доработки не может быть пустой' },
                ]}
              >
                <Input.TextArea
                  rows={2}
                  maxLength={500}
                  placeholder="Что необходимо исправить в комплекте"
                  data-testid="client-approval-rejection-reason"
                />
              </Form.Item>
            ) : null}

            <Form.Item
              name="comment"
              label="Комментарий"
              rules={
                commentRequired
                  ? [
                      { required: true, message: 'Укажите комментарий' },
                      { whitespace: true, message: 'Комментарий не может быть пустым' },
                    ]
                  : []
              }
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder={
                  commentRequired
                    ? 'Комментарий для журнала комплекта'
                    : 'Комментарий для журнала комплекта (необязательно)'
                }
                data-testid="client-approval-comment"
              />
            </Form.Item>
          </Form>

          <ClosingPackageTaskModalEventDateHint />
        </>
      )}
    </Modal>
  );
};

export default ClosingPackageClientApprovalModal;
